import React, { useEffect, useCallback, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useConfig from 'client/hooks/useConfig';
import { useFormContext } from '@ff/ui/Form2/Context';
import InputField from '@ff/ui/Form2/Fields/InputField'
import { styled } from '@ff/ui';
import Box from '@ff/ui/Box';
import Text from '@ff/ui/Text';
import url from 'client/utils/url';
import useReferrer from 'client/hooks/queries/useReferrer';
import debounce from 'lodash/debounce';
import modelCodes from 'client/constants/modelCode';

const borderImageBase64 = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYAgMAAACdGdVrAAAACVBMVEUAAAAAAAD///+D3c/SAAAAAnRSTlMAIE397fAAAAABYktHRAJmC3xkAAAAHUlEQVQI12MIZQBCRgYwQKccIAysFFYNNNEHdiEAhgkFCxuf3mIAAAAASUVORK5CYII=';

const useReferralCode = () => {
  const history = useHistory();
  const qs = useConfig();
  const { REFERRAL_CODE: referralCode='', ...rest } = qs;
  const { setFieldValue } = useFormContext();
  const { referrer, loading } = useReferrer(referralCode as string, {
    skip: !referralCode || referralCode.length < 6,
  });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedCheck = useCallback(
    debounce((checkedReferralCode: string) => {
      history.replace(url.getPageNavigationV3('summary', {
        ...rest,
        REFERRALCODE: checkedReferralCode,
      }));
    }, 500, { leading: false, trailing: true }),
    []
  );

  useEffect(() => {
    if(!loading){
      setFieldValue('referralCode', referrer ? referralCode : '');
      setFieldValue('displayedReferralCode', referralCode);
    }

  }, [loading, setFieldValue, referralCode, referrer]);

  return {
    referralCode,
    check: debouncedCheck,
    referrer,
    loading,
  }
}

export default function ReferralCodeBox({ modelCode }) {
  const { referralCode, check: checkReferralCode, referrer, loading } = useReferralCode();
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const modelName = Object.keys(modelCodes).find(k => modelCodes[k].fullModelCode === modelCode);
  const points = modelCodes[modelName].referralPoints;

  useEffect(() => {
    if(!loading){
      if(referralCode){
        if(referrer){
            setMessage(`Referral code of ${referrer.name}’s is accepted!`);
            setError('');
        }else{
          setError('The referral code is incorrect. Please verify your referral code and try again.');
        }
      }else{
        setMessage(`By using the Referral Code to purchase a car, you will receive ${points}F Co-creation Points.`);
        setError('');
      }
  }} ,[loading, referralCode, referrer, points]);

  const handleReferralCodeChanged = (event) => {
    checkReferralCode(event.target.value);
  }

  return (
    <Container>
      <Container2>
        <ReferralCodeInput
          name='displayedReferralCode'
          label='Referral Code'
          disabled={!!referrer}
          onChange={handleReferralCodeChanged}
        />
      </Container2>
      <Box height='28px'>
        <Message isError={!!error}>{error || message}</Message>
      </Box>
    </Container>
  );
}

const Container = styled(Box)({
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
});

const Container2 = styled(Box)({
  width: '100%',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '8px',
});

const ReferralCodeInput = styled(InputField)({
  height: '44.5px',
  flex: '1',
  textAlign: 'center',
  fontSize: '20px',
  fontWeight: 400,
  lineHeight: '24px',
  padding: '2px',

  ":disabled": {
    border: '8px solid transparent',
    borderImageSource: `url(${borderImageBase64})`, //tried to use a svg for borderImage, but it didn't work well.
    borderImageSlice: '8',
    borderImageRepeat: 'round',
  }
});

const Message = styled(Text)<{isError: boolean}>(({isError}) => ({
  position: 'relative',
  top: '-20px',
  fontSize: '12px',
  lineHeight: '14px',
  fontWeight: '400',
  color: isError ? '#BB4949' : '#000000',
}));